/**
 * SettingsPage — per-tenant config: company/sender details, invoice logo, and
 * invoice issuing defaults. GET/PUT /v1/settings; logo via PUT/GET /v1/settings/logo.
 * Foundational for invoice generation (Phase 3).
 *
 * The logo is fetched as a blob and shown via an object URL: the API is Bearer-authed
 * (no cookie), so a bare <img src="/v1/settings/logo"> would 401.
 */
import { useState, useEffect, useRef } from 'react'
import { apiErrorMessage } from '../../../../api/client'
import { toast } from 'sonner'
import { SectionTitle, Toggle, LoadingState, ErrorState, TabStrip } from '../../DashboardShared'
import { CurrencySelect } from '../../CurrencySelect'
import { useAsync, fetchSettings, updateSettings, uploadLogo, fetchLogoObjectUrl } from '../../api'
import type { TenantSettingsUpdate } from '../../types'
import { StageProbabilitiesCard } from './StageProbabilitiesCard'
import { QuotasCard } from './QuotasCard'
import { ExportsCard } from './ExportsCard'
import type { MeResponse } from '../../types'

/** Settings → tabs (2.2): Company = the invoice identity form below; Sales =
 *  the stage-probability ladder and, since 2.3b, the quotas card beneath it;
 *  Exports (§31 E-1) = the export audit, a PLATFORM record under the tenant's
 *  own gear — owner/admin read the table, everyone else reads a sentence and
 *  no request is made (E-2, decided from /v1/me, fail closed).
 *  Local state, one route — the gear button in the sidebar footer is the only
 *  entry, so no nav item moves. The strip itself is the shared TabStrip
 *  (DashboardShared): Reports lifted this page's tablist for its Forecast
 *  tab, so the pattern now lives in one place. */
type SettingsTab = 'company' | 'sales' | 'exports'
const SETTINGS_TABS = [{ id: 'company', label: 'Company' }, { id: 'sales', label: 'Sales' }, { id: 'exports', label: 'Exports' }] as const
const TAB_SUBTITLE: Record<SettingsTab, string> = {
  company: 'Company details, invoice branding, and issuing defaults',
  sales: 'Sales defaults for this workspace',
  exports: 'Who took what out of this workspace, and when',
}

const inputCls = 'w-full px-4 py-3 border border-edge-control rounded-control text-13 text-ink-primary placeholder-ink-section-label transition-all'
const labelCls = 'block text-12 font-semibold text-ink-body mb-2'

const ALLOWED_LOGO = ['image/png', 'image/jpeg', 'image/webp']
const LOGO_SOFT_LIMIT = 700 * 1024 // ~700KB — keeps the base64 body under nginx's 1MiB default

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {children}
    </div>
  )
}

export default function SettingsPage({ me = null }: { me?: MeResponse | null }) {
  const [tab, setTab] = useState<SettingsTab>('company')
  const { data, loading, error } = useAsync(fetchSettings, [])

  const [form, setForm] = useState<TenantSettingsUpdate>({
    senderName: '', senderEmail: '', senderPhone: '', senderAddress: '',
    vatNumber: '', paymentDetails: '',
    defaultPaymentTermsDays: 30, defaultCurrency: 'GBP', invoiceNumberPrefix: 'INV-',
    autoSend: false,
  })
  const [saving, setSaving] = useState(false)

  const [logoUrl, setLogoUrl] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const logoUrlRef = useRef<string | null>(null)

  function setLogoUrlSafe(url: string | null) {
    if (logoUrlRef.current) URL.revokeObjectURL(logoUrlRef.current)
    logoUrlRef.current = url
    setLogoUrl(url)
  }

  // seed the form from the fetched settings (GET returns sensible defaults on a fresh tenant)
  useEffect(() => {
    if (!data) return
    setForm({
      senderName: data.senderName ?? '',
      senderEmail: data.senderEmail ?? '',
      senderPhone: data.senderPhone ?? '',
      senderAddress: data.senderAddress ?? '',
      vatNumber: data.vatNumber ?? '',
      paymentDetails: data.paymentDetails ?? '',
      defaultPaymentTermsDays: data.defaultPaymentTermsDays ?? 30,
      defaultCurrency: data.defaultCurrency ?? 'GBP',
      invoiceNumberPrefix: data.invoiceNumberPrefix ?? 'INV-',
      autoSend: data.autoSend ?? false,
    })
  }, [data])

  // fetch the logo blob once we know one exists (Bearer auth → can't use a bare <img src>)
  useEffect(() => {
    let cancelled = false
    if (data?.hasLogo) {
      fetchLogoObjectUrl().then(url => {
        if (cancelled) { if (url) URL.revokeObjectURL(url); return }
        if (url) setLogoUrlSafe(url)
      })
    }
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.hasLogo])

  // revoke the last object URL on unmount
  useEffect(() => () => { if (logoUrlRef.current) URL.revokeObjectURL(logoUrlRef.current) }, [])

  const set = <K extends keyof TenantSettingsUpdate>(k: K, v: TenantSettingsUpdate[K]) =>
    setForm(f => ({ ...f, [k]: v }))

  async function handleSave() {
    setSaving(true)
    try {
      await updateSettings(form)
      toast.success('Settings saved')
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to save settings'))
    } finally {
      setSaving(false)
    }
  }

  async function handleLogoFile(file: File) {
    if (!ALLOWED_LOGO.includes(file.type)) { toast.error('Logo must be a PNG, JPEG, or WebP image'); return }
    if (file.size > LOGO_SOFT_LIMIT) { toast.error('Logo is too large — please use an image under ~700KB'); return }
    setUploading(true)
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result as string)
        reader.onerror = () => reject(new Error('Could not read file'))
        reader.readAsDataURL(file)
      })
      const comma = dataUrl.indexOf(',')
      const b64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl
      await uploadLogo(b64, file.type, file.name)
      setLogoUrlSafe(URL.createObjectURL(file))
      toast.success('Logo updated')
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to upload logo'))
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  return (
    <div className="page-enter flex flex-col gap-4 max-w-3xl">
      <SectionTitle
        title="Settings"
        subtitle={TAB_SUBTITLE[tab]}
        action={tab === 'company' ? <button className="k-btn k-btn--lg" onClick={handleSave}>{saving ? 'Saving…' : 'Save changes'}</button> : undefined}
      />
      <TabStrip<SettingsTab> label="Settings sections" tabs={SETTINGS_TABS} active={tab} onSelect={setTab} />
      {tab === 'sales' && <StageProbabilitiesCard />}
      {/* 2.3b (U-3): the quotas card is the ladder's sibling — same tab, same
          owner/admin-write / any-member-read posture, its own read below. */}
      {tab === 'sales' && <QuotasCard />}
      {/* §31: the export audit — its own read, its own gate sentence. */}
      {tab === 'exports' && <ExportsCard me={me} />}
      {/* The Company tab alone waits on GET /v1/settings; the Sales tab has its
          own reads — each card owns its loading — so a failed invoice-settings
          fetch never hides the ladder or the quotas. */}
      {tab === 'company' && loading && <LoadingState />}
      {tab === 'company' && !loading && error && <ErrorState message={error} />}
      {tab === 'company' && !loading && !error && <>

      <div className="k-card">
        <div className="p-5 flex flex-col gap-5">
          <div>
            <div className="text-13 font-semibold text-ink-primary">Company details</div>
            <div className="text-12 text-ink-muted mt-1">Shown as the sender on invoices you issue.</div>
          </div>
          <Field label="Sender name">
            <input type="text" value={form.senderName} onChange={e => set('senderName', e.target.value)} placeholder="e.g. Klaxiora Ltd" className={inputCls} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Sender email">
              <input type="email" value={form.senderEmail} onChange={e => set('senderEmail', e.target.value)} placeholder="billing@company.com" className={inputCls} />
            </Field>
            <Field label="Sender phone">
              <input type="tel" value={form.senderPhone} onChange={e => set('senderPhone', e.target.value)} placeholder="+44 …" className={inputCls} />
            </Field>
          </div>
          <Field label="Sender address">
            <textarea value={form.senderAddress} onChange={e => set('senderAddress', e.target.value)} rows={3} placeholder="Street, city, postcode" className={inputCls + ' resize-none'} />
          </Field>
          <Field label="VAT number">
            <input type="text" value={form.vatNumber} onChange={e => set('vatNumber', e.target.value)} placeholder="e.g. GB123456789" className={inputCls} />
          </Field>
        </div>
      </div>

      <div className="k-card">
        <div className="p-5 flex flex-col gap-4">
          <div>
            <div className="text-13 font-semibold text-ink-primary">Invoice logo</div>
            <div className="text-12 text-ink-muted mt-1">PNG, JPEG, or WebP, under ~700KB. Appears on your branded invoices.</div>
          </div>
          <div className="flex items-center gap-5">
            <div className="w-40 h-24 rounded-control border border-edge-control bg-surface-input flex items-center justify-center overflow-hidden flex-shrink-0">
              {logoUrl
                ? <img src={logoUrl} alt="Invoice logo" className="max-w-full max-h-full object-contain" />
                : <span className="text-11 text-ink-faint">No logo</span>}
            </div>
            <div>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) handleLogoFile(f) }} />
              <button className="k-btn-secondary" onClick={() => fileRef.current?.click()}>
                {uploading ? 'Uploading…' : logoUrl ? 'Replace logo' : 'Upload logo'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="k-card">
        <div className="p-5 flex flex-col gap-5">
          <div className="text-13 font-semibold text-ink-primary">Invoice defaults</div>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Currency">
              {/* Increment-3 picker: the capturable set replaces free text —
                  the server still refuses a default whose fixing went stale. */}
              <CurrencySelect className={inputCls} value={form.defaultCurrency} onChange={c => set('defaultCurrency', c)} />
            </Field>
            <Field label="Payment terms (days)">
              <input type="number" min={0} value={form.defaultPaymentTermsDays} onChange={e => set('defaultPaymentTermsDays', Number(e.target.value))} placeholder="30" className={inputCls} />
            </Field>
            <Field label="Number prefix">
              <input type="text" value={form.invoiceNumberPrefix} onChange={e => set('invoiceNumberPrefix', e.target.value)} placeholder="INV-" className={inputCls} />
            </Field>
          </div>
          <Field label="Payment details">
            <textarea value={form.paymentDetails} onChange={e => set('paymentDetails', e.target.value)} rows={3} placeholder="Bank name, sort code, account number, IBAN, or payment instructions" className={inputCls + ' resize-none'} />
          </Field>
          <div className="flex items-center justify-between border-t border-edge-row pt-4">
            <div>
              <div className="text-13 font-medium text-ink-primary">Auto-send invoices when due</div>
              <div className="text-12 text-ink-muted mt-1">Email generated invoices automatically instead of waiting for a manual send.</div>
            </div>
            <Toggle checked={form.autoSend} onChange={v => set('autoSend', v)} />
          </div>
        </div>
      </div>
      </>}
    </div>
  )
}
